import React, { useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';

import styles from './edit-users.module.css';
import { messages } from './messages';
import { FormattedCurrency } from '../../../../../../components/formatted-currency';
import { useManageSubscriptionDrawerContext } from '../../../context/manage-subscription-drawer-context';
import { useGetUserSeatsAddOnData } from '../../../hooks/use-get-user-seats-addon-data';
import { useGetNumberOfIncludedUserSeatsForPlan, useSelectedPlan, useGetMinNumberOfUserSeats } from '../../../hooks/use-plan-details';
import { useLocalizedPlanName } from '../../plan-name-helper';

export const EditAccountUsersContent = () => {
  const intl = useIntl();
  const { setIsUserAddOnModalOpen, desiredNumberOfAdditionalUsers, setDesiredNumberOfAdditionalUsers } =
    useManageSubscriptionDrawerContext();
  const selectedPlan = useSelectedPlan();
  const userSeatsAddOnData = useGetUserSeatsAddOnData();
  const addOnCost = userSeatsAddOnData.unitCost;

  const numberOfIncludedUserSeatsForSelectedPlan = useGetNumberOfIncludedUserSeatsForPlan(selectedPlan.billingPlanId);
  const minNumberOfSeatsToCoverActiveUsers = useGetMinNumberOfUserSeats(selectedPlan.billingPlanId);
  const shouldDisableDecrement =
    minNumberOfSeatsToCoverActiveUsers !== 0 && minNumberOfSeatsToCoverActiveUsers === desiredNumberOfAdditionalUsers;

  const onUserSetExtraSeats = useCallback(
    (numberExtraSeats) => {
      if (!numberExtraSeats) {
        setDesiredNumberOfAdditionalUsers(minNumberOfSeatsToCoverActiveUsers);
        return;
      }
      setDesiredNumberOfAdditionalUsers(numberExtraSeats);
    },
    [minNumberOfSeatsToCoverActiveUsers, setDesiredNumberOfAdditionalUsers],
  );

  const openDeactivateUserModal = useCallback(() => {
    setIsUserAddOnModalOpen(true);
  }, [setIsUserAddOnModalOpen]);

  const selectedPlanName = useLocalizedPlanName(selectedPlan?.name);

  return (
    <FlexColumn>
      <Text
        className={styles['users-description']}
        formattedMessageValues={{
          includedUserCount: numberOfIncludedUserSeatsForSelectedPlan,
          selectedPlanName,
          monthlyPrice: <FormattedCurrency currencyCode={addOnCost.code} value={addOnCost.value} />,
        }}
        textType={TextType.Paragraph}
        value={messages.includedUsersDescription}
      />
      {shouldDisableDecrement ? (
        <BannerMessageV2
          className={styles['edit-user-banner']}
          content={
            <Text
              element='span'
              formattedMessageValues={{
                extraUserCount: desiredNumberOfAdditionalUsers,
                planName: selectedPlanName,
                a: (chunks) => (
                  <Button onClick={openDeactivateUserModal} theme='link'>
                    {chunks}
                  </Button>
                ),
              }}
              textType={TextType.Caption}
              value={messages.howToRemoveUser}
            />
          }
          layout={BannerLayout.Inline}
          status={BannerStatus.Info}
        />
      ) : null}
      <FlexRow>
        <FlexColumn ariaLabel={intl.formatMessage(messages.includedUsersLabel)}>
          <Text className={styles['user-interaction-label']} textType={TextType.H4} value={messages.includedUsersLabel} />
          <Text className={styles['user-count']} textType={TextType.Paragraph} value={numberOfIncludedUserSeatsForSelectedPlan} />
        </FlexColumn>
        <FlexColumn>
          <Text className={styles['user-interaction-label']} textType={TextType.H4} value={messages.additionalUsersLabel} />
          <NumberInput
            className={styles['user-spinner']}
            disabledDecrementTooltipMessage={<FormattedMessage {...messages.decrementUserDisabledMessage} />}
            label={intl.formatMessage(messages.additionalUsersLabel)}
            min={minNumberOfSeatsToCoverActiveUsers}
            onChange={onUserSetExtraSeats}
            shouldDisableOnMinOrMax={shouldDisableDecrement}
            value={desiredNumberOfAdditionalUsers}
          />
        </FlexColumn>
        <FlexColumn ariaLabel={intl.formatMessage(messages.totalUsersLabel)}>
          <Text className={styles['user-interaction-label']} textType={TextType.H4} value={messages.totalUsersLabel} />
          <Text
            className={styles['user-count']}
            textType={TextType.Paragraph}
            value={desiredNumberOfAdditionalUsers + numberOfIncludedUserSeatsForSelectedPlan}
          />
        </FlexColumn>
      </FlexRow>
    </FlexColumn>
  );
};
